// ============================================================================
// vpn/VpnSshBlock.tsx — «SSH-доступ»: понятная пошаговая инструкция подключения
// к ВСТРОЕННОМУ SSH-серверу платформы (ядро поднимает его при активном профиле
// с протоколом «ssh»; настройка базы ssh.enabled/host/port — в свойствах базы).
// Данные — SSoT сервера (VpnService.GetSshAccess): команды, хост-ключ, пробросы.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { Copy, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getSDK } from '@/store/sdkStore';
import { createLogger } from '@/lib/logger';
import { copyToClipboard } from '@/lib/clipboard';
import { eventBusHub } from '@/lib/eventBusHub';

const log = createLogger('VpnSshBlock');

interface SshAccess {
  available: boolean;
  enabled: boolean;
  host: string;
  port: number;
  login: string;
  hostKey: string;
  command: string;
  localForward: string;
  remoteForward: string;
  keyRegistered: boolean;
  message: string;
}

export function VpnSshBlock() {
  const [access, setAccess] = useState<SshAccess | null>(null);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await getSDK().vpn.getSshAccess({});
      setAccess({
        available: !!r.available, enabled: !!r.enabled, host: r.host, port: Number(r.port),
        login: r.login, hostKey: r.hostKey, command: r.command, localForward: r.localForward,
        remoteForward: r.remoteForward, keyRegistered: !!r.keyRegistered, message: r.message,
      });
    } catch (e) {
      log.error('get ssh access', { error: e });
      setAccess(null);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  // Живое обновление: профили/настройки VPN менялись (шина) — перечитать.
  useEffect(() => {
    const unsub = eventBusHub.subscribe((ev) => {
      const topic = ev.topic || '';
      if (topic.startsWith('vpn.profile.') || topic === 'vpn.settings.updated') void load();
    });
    return unsub;
  }, [load]);

  const copy = (text: string, what: string) => {
    if (!text) return;
    void copyToClipboard(text).catch((e) => log.error('copy ssh', { error: e, what }));
  };

  if (!loaded) {
    return <div className="rounded-md border p-3 text-xs text-muted-foreground">SSH-доступ: загрузка…</div>;
  }
  if (!access || !access.available) {
    return (
      <div className="rounded-md border p-3 text-xs text-muted-foreground">
        SSH-доступ: {access?.message || 'недоступен — обратитесь к администратору'}
      </div>
    );
  }

  const addr = `${access.host}:${access.port}`;

  return (
    <div className="rounded-md border p-3 space-y-3">
      <div className="flex items-center gap-2">
        <div className="text-sm font-medium">SSH-доступ (встроенный сервер платформы)</div>
        <span className={access.enabled ? 'ml-auto text-xs text-green-600' : 'ml-auto text-xs text-amber-600'}>
          {access.enabled ? `включён · ${addr}` : 'выключен'}
        </span>
        <Button variant="ghost" size="sm" onClick={() => void load()} title="Обновить">
          <RefreshCw className="h-3.5 w-3.5" />
        </Button>
      </div>

      {!access.enabled && (
        <div className="rounded border border-amber-600/40 bg-amber-600/5 p-2 text-xs text-amber-700">
          Сервер поднимается ЯДРОМ автоматически, когда есть АКТИВНЫЙ профиль с протоколом «ssh»
          (вкладка «Профили»). Порт и адрес — настройки базы: Параметры → ssh.enabled / ssh.host / ssh.port.
          {access.message ? ` ${access.message}` : ''}
        </div>
      )}

      <ol className="space-y-2 text-xs list-none">
        <li className="space-y-1">
          <div><span className="font-medium">1. Подключитесь по команде</span> (логин — ваша учётная запись платформы):</div>
          <CommandRow text={access.command} onCopy={() => copy(access.command, 'command')} />
        </li>
        <li className="space-y-1">
          <div><span className="font-medium">2. Вход:</span> пароль — обычный пароль входа в приложение.
            {access.keyRegistered
              ? ' Ключ устройства зарегистрирован — можно входить без пароля.'
              : ' Либо добавьте устройство с открытым ключом (Профиль → «Мои устройства») и входите по ключу.'}</div>
        </li>
        <li className="space-y-1">
          <div><span className="font-medium">3. Проверьте подлинность сервера</span> — сверьте хост-ключ
            (первое подключение):</div>
          <pre className="overflow-x-auto rounded bg-muted/60 p-2">{access.hostKey || '—'}</pre>
          <Button variant="outline" size="sm" onClick={() => copy(access.hostKey, 'hostkey')}>
            <Copy className="h-3 w-3 mr-1" /> Копировать хост-ключ
          </Button>
        </li>
        <li className="space-y-1">
          <div><span className="font-medium">4. Проброс портов</span> — доступ к сервисам платформы:</div>
          <div className="text-muted-foreground">Локальный (сервис платформы как будто на вашем компьютере):</div>
          <CommandRow text={access.localForward} onCopy={() => copy(access.localForward, 'local')} />
          <div className="text-muted-foreground">Удалённый (ваш локальный порт доступен на сервере):</div>
          <CommandRow text={access.remoteForward} onCopy={() => copy(access.remoteForward, 'remote')} />
        </li>
      </ol>
    </div>
  );
}

function CommandRow({ text, onCopy }: { text: string; onCopy: () => void }) {
  if (!text) return <div className="text-muted-foreground">—</div>;
  return (
    <div className="flex items-center gap-2">
      <code className="flex-1 overflow-x-auto rounded bg-muted/60 px-2 py-1">{text}</code>
      <Button variant="outline" size="sm" onClick={onCopy}><Copy className="h-3 w-3" /></Button>
    </div>
  );
}
